import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import ProfileStatusService from '../services/ProfileStatusService';

/**
 * Custom hook for profile status management
 * 
 * This hook combines all necessary data from Redux store and provides
 * a clean interface for components to access profile status information.
 */
export const useProfileStatus = (isProfileCompletedFn, hasSubjects = true, hasPaid = false, cancelAtPeriodEnd = false) => {
  // Get all necessary data from Redux store
  const profileUser = useSelector((state) => state.profile.user);
  const { _id, status, isSubmitted } = profileUser;
  const { userId } = useSelector((state) => state.main);
  const { profileDisabled, adminDisabled } = useSelector((state) => state.main);
  const { status: accountStatus } = useSelector((state) => state.main);

  // Memoized status calculation
  const statusInfo = useMemo(() => {
    try {
      const result = ProfileStatusService.getStatus({
        _id,
        status,
        isSubmitted,
        profileUser,
        accountStatus,
        profileDisabled,
        adminDisabled,
        hasPaid,
        hasSubjects,
        cancelAtPeriodEnd,
        isProfileCompleted: isProfileCompletedFn ? isProfileCompletedFn(profileUser, hasSubjects) : false
      });
      
      // Ensure we always return a valid object
      return result || {
        status: 'UNKNOWN',
        message: 'Profile status is being processed.',
        color: '#666666',
        actionable: false
      };
    } catch (error) {
      console.error('Error in useProfileStatus:', error);
      return {
        status: 'ERROR',
        message: 'Error loading profile status.',
        color: '#da1e28',
        actionable: false
      };
    }
  }, [
    _id,
    status,
    isSubmitted,
    profileUser,
    accountStatus,
    profileDisabled,
    adminDisabled,
    hasPaid,
    hasSubjects,
    cancelAtPeriodEnd,
    isProfileCompletedFn
  ]);

  // Memoized utility functions
  const utils = useMemo(() => ({
    canEdit: () => ProfileStatusService.canEdit(statusInfo.status),
    isVisible: () => ProfileStatusService.isVisible(statusInfo.status),
    requiresApproval: () => ProfileStatusService.requiresApproval(statusInfo.status),
    getColor: () => ProfileStatusService.getStatusColor(statusInfo.status),
    getMessage: () => ProfileStatusService.getStatusMessage(statusInfo.status)
  }), [statusInfo.status]);

  return {
    ...statusInfo,
    utils
  };
};

export default useProfileStatus;
